<script setup>
// 通用 Pro 付费拦截弹窗(#21 design 定稿:统一固定文案 —— 所有 Pro 闸口 batch/embroidery/… 都开这一个,文案不随触发场景变)。
// 任何 Pro 功能(登录后点付费操作 / 批量超额)经 openPaywall() 弹出。
// 自包含样式(不依赖页面 CSS 作用域),Teleport 到 body 避免被容器 overflow/stacking 截断。
const props = defineProps({
  open: { type: Boolean, default: false },
  // formats prop 已停用(design 改通用固定文案,不再渲染格式 chip);保留以兼容旧调用点 openPaywall([...])。
  formats: { type: Array, default: () => [] },
});
const emit = defineEmits(["close", "view-plans"]);
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="pw-overlay" @click.self="emit('close')">
      <div class="pw-modal" role="dialog" aria-modal="true" aria-labelledby="pwTitle">
        <button class="pw-close" type="button" aria-label="Close" @click="emit('close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
        </button>

        <div class="pw-top">
          <div class="pw-badge">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.5 6.5L21 9l-5 4.5L17.5 21 12 17l-5.5 4L8 13.5 3 9l6.5-.5z" /></svg>
            Pro feature
          </div>
          <div class="pw-lock">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="11" rx="2.2" /><path d="M8 10V7a4 4 0 018 0v3" /><circle cx="12" cy="15.5" r="1.4" fill="currentColor" stroke="none" /></svg>
          </div>
          <h2 id="pwTitle">Upgrade to SharkFoto Pro</h2>
          <p>This is a <b>Pro feature</b>. Upgrade to unlock it — along with everything else Pro across SharkFoto.</p>
        </div>

        <div class="pw-benefits">
          <div class="b">
            <span class="ck"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg></span>
            <span><b>Batch conversion</b> — convert &amp; download many files at once</span>
          </div>
          <div class="b">
            <span class="ck"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg></span>
            <span><b>Embroidery digitising</b> — export to DST, PES, EXP &amp; more</span>
          </div>
          <div class="b">
            <span class="ck"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg></span>
            <span><b>No watermark</b> on every download</span>
          </div>
          <div class="b">
            <span class="ck"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg></span>
            <span>Monthly credits for <b>AI image, video &amp; music tools</b></span>
          </div>
        </div>

        <div class="pw-actions">
          <button class="pw-btn pw-btn-primary" type="button" @click="emit('view-plans')">
            View Plans
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </button>
          <button class="pw-btn pw-btn-ghost" type="button" @click="emit('close')">Maybe later</button>
        </div>

        <div class="pw-foot">Cancel anytime · plans start with a monthly credit allowance.</div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.pw-overlay {
  --brand: #8b5cf6; --brand-2: #b06bfb; --emb: #c2548a; --gold: #fbbf24; --green: #34d399;
  --tx: #f4f6fa; --tx-dim: rgba(244, 246, 250, 0.62); --tx-faint: rgba(244, 246, 250, 0.42);
  --line: rgba(255, 255, 255, 0.09); --line-strong: rgba(255, 255, 255, 0.16);
  --font-h: "Space Grotesk", system-ui, sans-serif; --font-b: "Public Sans", system-ui, sans-serif;
  position: fixed; inset: 0; background: rgba(4, 5, 9, 0.72); backdrop-filter: blur(6px);
  display: grid; place-items: center; padding: 24px; z-index: 9999;
}
.pw-overlay, .pw-overlay * { box-sizing: border-box; }
.pw-modal {
  width: 100%; max-width: 460px; background: linear-gradient(180deg, #13121c, #0e0d15);
  border: 1px solid var(--line-strong); border-radius: 22px; overflow: hidden;
  box-shadow: 0 40px 100px -20px rgba(0, 0, 0, 0.85); position: relative; color: var(--tx); font-family: var(--font-b);
}
.pw-modal::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 160px; background: radial-gradient(80% 100% at 50% 0%, color-mix(in oklab, var(--brand) 30%, transparent), transparent 70%); pointer-events: none; }
.pw-close { position: absolute; top: 16px; right: 16px; width: 34px; height: 34px; border-radius: 9px; border: 1px solid var(--line); background: rgba(255, 255, 255, 0.03); color: var(--tx-faint); display: grid; place-items: center; cursor: pointer; transition: 0.15s; z-index: 2; }
.pw-close:hover { color: var(--tx); background: rgba(255, 255, 255, 0.08); }
.pw-close svg { width: 17px; height: 17px; }
.pw-top { position: relative; padding: 36px 34px 8px; text-align: center; z-index: 1; }
.pw-badge { display: inline-flex; align-items: center; gap: 7px; padding: 6px 13px 6px 9px; border: 1px solid color-mix(in oklab, var(--gold) 45%, var(--line-strong)); border-radius: 999px; background: color-mix(in oklab, var(--gold) 12%, transparent); font-family: var(--font-h); font-size: 11.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--gold); margin-bottom: 22px; }
.pw-badge svg { width: 13px; height: 13px; }
.pw-lock { width: 66px; height: 66px; border-radius: 19px; margin: 0 auto 20px; display: grid; place-items: center; background: linear-gradient(135deg, var(--brand), var(--brand-2)); box-shadow: 0 12px 30px -8px color-mix(in oklab, var(--brand) 70%, transparent); }
.pw-lock svg { width: 32px; height: 32px; color: #fff; }
.pw-top h2 { font-family: var(--font-h); font-weight: 600; font-size: 24px; margin-bottom: 10px; letter-spacing: -0.025em; line-height: 1.12; }
.pw-top p { color: var(--tx-dim); font-size: 14.5px; line-height: 1.55; max-width: 340px; margin: 0 auto; }
.pw-top p b { color: var(--tx); }
.pw-fmts { display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; padding: 20px 34px 4px; }
.pw-chip { display: inline-flex; align-items: center; gap: 5px; font-family: var(--font-h); font-size: 11.5px; font-weight: 600; color: color-mix(in oklab, var(--emb) 75%, white); background: color-mix(in oklab, var(--emb) 15%, transparent); border: 1px solid color-mix(in oklab, var(--emb) 35%, transparent); border-radius: 7px; padding: 4px 9px; }
.pw-benefits { margin: 22px 34px 4px; padding: 16px 18px; background: rgba(255, 255, 255, 0.025); border: 1px solid var(--line); border-radius: 14px; display: grid; gap: 11px; }
.pw-benefits .b { display: flex; align-items: flex-start; gap: 11px; font-size: 13.5px; color: var(--tx-dim); }
.pw-benefits .b b { color: var(--tx); font-weight: 600; }
.pw-benefits .b .ck { width: 19px; height: 19px; flex: none; border-radius: 6px; background: color-mix(in oklab, var(--green) 18%, transparent); color: var(--green); display: grid; place-items: center; margin-top: 1px; }
.pw-benefits .b .ck svg { width: 12px; height: 12px; }
.pw-actions { padding: 22px 34px 16px; display: grid; gap: 10px; }
.pw-btn { display: flex; align-items: center; justify-content: center; gap: 8px; font-family: var(--font-b); font-weight: 700; font-size: 15px; border-radius: 12px; padding: 14px 20px; cursor: pointer; border: 1px solid transparent; transition: 0.18s; width: 100%; }
.pw-btn-primary { background: linear-gradient(135deg, var(--brand), var(--brand-2)); color: #fff; box-shadow: 0 10px 26px -8px color-mix(in oklab, var(--brand) 65%, transparent); }
.pw-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 14px 32px -8px color-mix(in oklab, var(--brand) 75%, transparent); }
.pw-btn-ghost { background: transparent; color: var(--tx-dim); padding: 10px; font-size: 14px; }
.pw-btn-ghost:hover { color: var(--tx); }
.pw-foot { text-align: center; padding: 0 34px 26px; font-size: 12px; color: var(--tx-faint); line-height: 1.5; }
</style>
